import { FC, useState, FormEvent } from 'react';
import { CheckCircle, XCircle, Ban, RefreshCw } from 'lucide-react';
import { Modal } from '../../ui/Modal/Modal';
import { cn } from '../../../utils/cn';

export type ActionType = 'approve' | 'reject' | 'suspend' | 'reactivate';

interface StatusActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: { reason?: string; notes?: string; cancelFutureBookings?: boolean }) => void;
  action: ActionType;
  therapistName: string;
  isLoading?: boolean;
}

const actionConfig: Record<
  ActionType,
  {
    title: string;
    description: string;
    icon: typeof CheckCircle;
    iconBg: string;
    iconColor: string;
    buttonBg: string;
    buttonText: string;
    requiresReason: boolean;
    hasNotesField: boolean;
    hasCancelBookingsOption: boolean;
  }
> = {
  approve: {
    title: 'Approve Therapist',
    description: 'This will allow the therapist to accept bookings.',
    icon: CheckCircle,
    iconBg: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'text-green-600 dark:text-green-400',
    buttonBg: 'bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600',
    buttonText: 'Approve',
    requiresReason: false,
    hasNotesField: true,
    hasCancelBookingsOption: false,
  },
  reject: {
    title: 'Reject Therapist',
    description: 'This will reject the therapist application. They will not be able to accept bookings.',
    icon: XCircle,
    iconBg: 'bg-red-100 dark:bg-red-900/30',
    iconColor: 'text-red-600 dark:text-red-400',
    buttonBg: 'bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600',
    buttonText: 'Reject',
    requiresReason: true,
    hasNotesField: false,
    hasCancelBookingsOption: false,
  },
  suspend: {
    title: 'Suspend Therapist',
    description: 'This will temporarily prevent the therapist from accepting new bookings.',
    icon: Ban,
    iconBg: 'bg-orange-100 dark:bg-orange-900/30',
    iconColor: 'text-orange-600 dark:text-orange-400',
    buttonBg: 'bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600',
    buttonText: 'Suspend',
    requiresReason: true,
    hasNotesField: false,
    hasCancelBookingsOption: true,
  },
  reactivate: {
    title: 'Reactivate Therapist',
    description: 'This will allow the therapist to accept bookings again.',
    icon: RefreshCw,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
    buttonBg: 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
    buttonText: 'Reactivate',
    requiresReason: false,
    hasNotesField: true,
    hasCancelBookingsOption: false,
  },
};

export const StatusActionModal: FC<StatusActionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  action,
  therapistName,
  isLoading = false,
}) => {
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [cancelFutureBookings, setCancelFutureBookings] = useState(false);
  const [error, setError] = useState('');

  const config = actionConfig[action];
  const Icon = config.icon;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (config.requiresReason && !reason.trim()) {
      setError('Please provide a reason');
      return;
    }

    onConfirm({
      reason: config.requiresReason ? reason.trim() : undefined,
      notes: config.hasNotesField ? notes.trim() || undefined : undefined,
      cancelFutureBookings: config.hasCancelBookingsOption ? cancelFutureBookings : undefined,
    });
  };

  const handleClose = () => {
    if (!isLoading) {
      setReason('');
      setNotes('');
      setCancelFutureBookings(false);
      setError('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" closable={!isLoading}>
      <form onSubmit={handleSubmit}>
        <div className="sm:flex sm:items-start">
          <div
            className={cn(
              'mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10',
              config.iconBg
            )}
          >
            <Icon className={cn('h-6 w-6', config.iconColor)} />
          </div>
          <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-slate-100">
              {config.title}
            </h3>
            <div className="mt-2">
              <p className="text-sm text-gray-500 dark:text-slate-400">
                {config.description}
              </p>
              <p className="mt-2 text-sm font-medium text-gray-900 dark:text-slate-100">
                Therapist: {therapistName}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          {/* Reason field (required for reject/suspend) */}
          {config.requiresReason && (
            <div>
              <label
                htmlFor="reason"
                className="block text-sm font-medium text-gray-700 dark:text-slate-300"
              >
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className={cn(
                  'mt-1 block w-full rounded-md border px-3 py-2 text-sm',
                  'bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100',
                  'focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-900',
                  error
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 dark:border-slate-600 focus:border-blue-500 focus:ring-blue-500'
                )}
                placeholder={`Enter reason for ${action}...`}
              />
              {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
            </div>
          )}

          {/* Notes field (optional for approve/reactivate) */}
          {config.hasNotesField && (
            <div>
              <label
                htmlFor="notes"
                className="block text-sm font-medium text-gray-700 dark:text-slate-300"
              >
                Notes (optional)
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="mt-1 block w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-slate-100 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
                placeholder="Add any notes..."
              />
            </div>
          )}

          {/* Cancel future bookings checkbox (for suspend) */}
          {config.hasCancelBookingsOption && (
            <div className="flex items-start">
              <input
                id="cancelBookings"
                type="checkbox"
                checked={cancelFutureBookings}
                onChange={(e) => setCancelFutureBookings(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
              />
              <label
                htmlFor="cancelBookings"
                className="ml-2 text-sm text-gray-700 dark:text-slate-300"
              >
                Cancel all future bookings for this therapist
              </label>
            </div>
          )}
        </div>

        <div className="mt-6 sm:flex sm:flex-row-reverse sm:gap-3">
          <button
            type="submit"
            disabled={isLoading}
            className={cn(
              'inline-flex w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:text-sm',
              config.buttonBg
            )}
          >
            {isLoading ? (
              <>
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Processing...
              </>
            ) : (
              config.buttonText
            )}
          </button>
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-4 py-2 text-base font-medium text-gray-700 dark:text-slate-300 shadow-sm hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 disabled:cursor-not-allowed disabled:opacity-50 sm:mt-0 sm:w-auto sm:text-sm"
          >
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default StatusActionModal;
